import { Link } from 'react-router-dom'
import { Clock, Users, Star, Wrench } from 'lucide-react'
import { Recipe } from '@/types/recipe'
import { formatTime } from '@/lib/utils'
import { getEquipmentIcon } from '@/lib/equipment'
import { getMealTypeIcon, getMealTypeColor } from '@/lib/meal-types'
import { Card, CardContent } from '@/components/ui/card'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'

interface RecipeCardProps {
  recipe: Recipe
  showAuthor?: boolean
  showStats?: boolean
  className?: string
}

export function RecipeCard({ recipe, showAuthor = false, className = '' }: RecipeCardProps) {
  const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0)
  const sourceLabel = recipe.is_public
    ? 'Publiek'
    : recipe.household_id
      ? 'Huishouden'
      : 'Persoonlijk'

  return (
    <Link to={`/recepten/${recipe.id}`}>
      <Card
        className={`group h-full overflow-hidden transition-all duration-200 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600 ${className}`}
      >
        <div className="relative aspect-video overflow-hidden">
          {recipe.image_url ? (
            <img
              src={recipe.image_url}
              alt={recipe.title}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-warm-100 to-warm-200">
              <span className="text-4xl text-warm-400">🍽️</span>
            </div>
          )}

          {recipe.meal_type && (
            <div className="absolute left-2 top-2">
              <span className={`rounded-full px-2 py-1 text-xs font-medium ${getMealTypeColor(recipe.meal_type)}`}>
                {getMealTypeIcon(recipe.meal_type)}
              </span>
            </div>
          )}

          {recipe.difficulty ? (
            <div className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 shadow-sm backdrop-blur">
              <DifficultyIndicator difficulty={recipe.difficulty} showLabel={false} />
            </div>
          ) : null}
        </div>

        <CardContent className="p-4">
          <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-gray-900 transition-colors group-hover:text-primary-600 dark:text-slate-50">
            {recipe.title}
          </h3>

          {recipe.description && (
            <p className="mb-3 line-clamp-2 text-sm text-gray-600 dark:text-slate-200">
              {recipe.description}
            </p>
          )}

          {showAuthor && (
            <div className="mb-3">
              <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-slate-800 dark:text-slate-100">
                {sourceLabel}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-slate-200">
            <div className="flex items-center space-x-4">
              {totalTime > 0 && (
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(totalTime)}</span>
                </div>
              )}

              {recipe.servings && (
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{recipe.servings}</span>
                </div>
              )}
            </div>

            {recipe.rating && (
              <div className="flex items-center space-x-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span>{recipe.rating}</span>
              </div>
            )}
          </div>

          {recipe.equipment.length > 0 && (
            <div className="mt-3 flex items-center gap-1">
              <Wrench className="h-3 w-3 text-gray-400 dark:text-slate-300" />
              <div className="flex gap-1">
                {recipe.equipment.slice(0, 4).map((equipment) => (
                  <span key={equipment} className="text-sm" title={equipment}>
                    {getEquipmentIcon(equipment)}
                  </span>
                ))}
                {recipe.equipment.length > 4 && (
                  <span className="text-xs text-gray-500 dark:text-slate-200">+{recipe.equipment.length - 4}</span>
                )}
              </div>
            </div>
          )}

          {recipe.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {recipe.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 dark:bg-orange-950 dark:text-orange-100"
                >
                  {tag}
                </span>
              ))}
              {recipe.tags.length > 3 && (
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-slate-800 dark:text-slate-100">
                  +{recipe.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}
